import qs from "qs"
import { Fragment, useEffect, useState, useCallback } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, ToggleLeftIcon, ToggleRightIcon, Trash2Icon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData, useSearchParams } from "react-router"
import { useForm } from "react-hook-form"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse, reactHookFormSubmitShim, throwIfNotOk } from "@/utils"
import { FiltersWrapper, type StoredSession, type OnBeforeSubmit } from "@/components/Filters"
import type { RootLoader } from "../root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { hasPermission } from "@/machines/Authmachine"
import Pagination from "@/components/ui/pagination"
import z from "zod"
import {
  formatFluentValidationErrors,
  formatRHFErrors,
  type FluentValidationError,
  type ValidationSummaryData,
} from "@/utils/forms"
import { ValidationSummary } from "@/components/ui/ValidationSummary"
import { zodResolver } from "@hookform/resolvers/zod"
import { Badge } from "@/components/ui/badge"

const routeId = "review-questions-routeId"
const sessionKey = "review-questions-filter"

interface ReviewQuestion {
  active: boolean
  code: string
  name: string
  organisation: string
  value: string
}

interface ActionReturn {
  action: "CREATE" | "UPDATE" | "DELETE" | "TOGGLEACTIVE" | "UNKNOWN"
  errorMessage?: string
  errors: Record<string, string[]>
}

interface ReviewQuestionAPIResponse {
  items: ReviewQuestion[]
  page: number
  totalCount: number
}

const createValidationSchema = <T extends "CREATE" | "UPDATE">(action: T) => {
  return z.object({
    action: z.literal(action),
    organisation: z.string().trim().nonempty(),
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .max(100, "Name should be 100 characters or less")
      .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed"),
    code: z
      .string()
      .trim()
      .min(1, "Code is required")
      .max(50, "Code should be 50 characters or less")
      .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed"),
    value: z
      .string()
      .trim()
      .min(1, "Question text is required")
      .max(1000, "Question text should be 1000 characters or less"),
  })
}

const QuestionCreateSchema = createValidationSchema("CREATE")
const QuestionUpdateSchema = createValidationSchema("UPDATE")

const QuestionDeleteSchema = z.object({
  action: z.literal("DELETE"),
  organisation: z.string().trim().nonempty(),
  name: z.string().trim().nonempty(),
})

const QuestionToggleActiveSchema = z.object({
  action: z.literal("TOGGLEACTIVE"),
  organisation: z.string().trim().nonempty(),
  name: z.string().trim().nonempty(),
  active: z.preprocess((val) => {
    if (typeof val === "string") {
      if (val.toLowerCase() === "true") return true
      if (val.toLowerCase() === "false") return false
    }
    return val
  }, z.boolean()),
})

const QuestionEditOrCreateSchema = z.discriminatedUnion("action", [QuestionCreateSchema, QuestionUpdateSchema])

const QuestionSchema = z.discriminatedUnion("action", [
  QuestionCreateSchema,
  QuestionUpdateSchema,
  QuestionDeleteSchema,
  QuestionToggleActiveSchema,
])

async function loader({ request }: { request: Request }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const url = new URL(request.url)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]
  const includeInactive = filters.parsed?.includeInactive ?? false

  try {
    const response = await fetchAPI<ReviewQuestionAPIResponse>(
      `/api/unityReview/Question/${loadedOrganisation}/${page}/${pageSize}?includeInactive=${includeInactive}`,
    ).then(handleResponse)

    return {
      reviewQuestions: response.items,
      loadedOrganisation,
      totalCount: response.totalCount,
      page: response.page,
    }
  } catch (error) {
    if (error != null && typeof error === "object" && "status" in error && error.status === 404) {
      return { reviewQuestions: [], loadedOrganisation, totalCount: 0, page: 1 }
    }
    throw error
  }
}

async function mainAction({ request }: { request: Request }): Promise<ActionReturn> {
  const rawBody = qs.parse(await request.text())
  const { data, success, error } = QuestionSchema.safeParse(rawBody)

  if (!success) {
    console.log(error)
    return {
      action: "UNKNOWN", // We can't trust the form data, so use a default value
      errorMessage: "Failed to parse the form contents",
      errors: {},
    }
  }

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  switch (data.action) {
    case "CREATE":
    case "UPDATE": {
      const { action, organisation, ...payload } = data

      try {
        await fetchAPI(`/api/unityReview/Question/${organisation}`, {
          method: "POST",
          body: JSON.stringify(payload),
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = action === "CREATE" ? "Error creating question" : "Error updating question"
        }
      }
      break
    }
    case "DELETE": {
      try {
        await fetchAPI(`/api/unityReview/Question/${data.organisation}/${data.name}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = "Error deleting review question"
        }
      }
      break
    }
    case "TOGGLEACTIVE": {
      try {
        await fetchAPI(`/api/unityReview/Question/${data.organisation}/${data.name}`, {
          method: "PUT",
          body: JSON.stringify({ active: !data.active }),
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = "Error toggling question active status"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

function ToggleActive({
  question,
  onError,
  onSuccess,
}: {
  question: ReviewQuestion
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const { register, handleSubmit } = useForm<
    z.input<typeof QuestionToggleActiveSchema>,
    any,
    z.output<typeof QuestionToggleActiveSchema>
  >({
    resolver: zodResolver(QuestionToggleActiveSchema),
    values: {
      action: "TOGGLEACTIVE" as const,
      organisation: question.organisation,
      name: question.name,
      active: question.active,
    },
  })
  const fetcher = useFetcher<ActionReturn>({ key: "q_silent_forms" })

  const isSubmitting = fetcher.state !== "idle"

  const onSubmit = handleSubmit(
    (formData) => {
      // This will ONLY run if Zod is 100% happy
      // Clear any zod errors down.
      onSuccess()
      reactHookFormSubmitShim(formData, fetcher.submit)
    },
    (errors) => {
      onError(formatRHFErrors(errors))
    },
  )

  return (
    <fetcher.Form method="POST" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />
      <input type="hidden" {...register("name")} />
      <input type="hidden" {...register("active")} />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            disabled={isSubmitting}
            size="icon"
            variant="ghost"
            type="submit"
            className="h-8 w-8 cursor-pointer opacity-50"
          >
            {question.active ? (
              <ToggleRightIcon className="h-4 w-4 text-green-600" />
            ) : (
              <ToggleLeftIcon className="h-4 w-4 text-gray-600" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{question.active ? "Deactivate" : "Activate"}</TooltipContent>
      </Tooltip>
    </fetcher.Form>
  )
}

function QuestionDeleteForm({
  question,
  onError,
  onSuccess,
}: {
  question: ReviewQuestion
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const { register, handleSubmit } = useForm<z.infer<typeof QuestionDeleteSchema>>({
    resolver: zodResolver(QuestionDeleteSchema),
    values: {
      action: "DELETE" as const,
      organisation: question.organisation,
      name: question.name,
    },
  })
  const fetcher = useFetcher<ActionReturn>({ key: "q_silent_forms" })

  const onSubmit = handleSubmit(
    (formData) => {
      onSuccess()
      reactHookFormSubmitShim(formData, fetcher.submit)
    },
    (errors) => {
      onError(formatRHFErrors(errors))
    },
  )

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Question</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          Question &quot;<span className="font-semibold">{question.name}</span>&quot; will be deleted. Are you sure?
        </p>
        <fetcher.Form
          method="DELETE"
          className="flex items-center justify-start gap-3 pt-4"
          onSubmit={(e) => void onSubmit(e)}
        >
          <input type="hidden" {...register("action")} />
          <input type="hidden" {...register("organisation")} />
          <input type="hidden" {...register("name")} />
          {fetcher.state !== "idle" && fetcher.formData?.get("name") === question.name && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button variant="ghost">Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="destructive">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()
  const url = new URL(window.location.href)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const [globalErrors, setGlobalErrors] = useState<Record<string, string[]>>({})
  const fetcher = useFetcher<ActionReturn>({ key: "q_silent_forms" })

  const serverErrors = fetcher.data?.errors
  const combinedErrors = { ...globalErrors, ...serverErrors }

  const pagingAdjustment = (page - 1) * pageSize

  return (
    <div className="h-screen overflow-y-auto">
      <Pagination routeId={routeId} />
      <ValidationSummary errors={combinedErrors} />
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Name</TableHead>
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Code</TableHead>
            <TableHead className="text-left">Value</TableHead>
            <TableHead className="w-24 text-left">Active</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.reviewQuestions.length > 0 ? (
            data.reviewQuestions.map((item, index) => (
              <TableRow key={`${item.organisation}-${item.name}`} className="text-sm">
                <TableCell>{index + 1 + pagingAdjustment}.</TableCell>
                <TableCell className="break-words whitespace-normal">{item.name}</TableCell>
                <TableCell className="break-words whitespace-normal">{item.code}</TableCell>
                <TableCell className="overflow-hidden text-ellipsis">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">{item.value}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-2xl p-4 break-words whitespace-normal" side="top">
                      <p className="text-sm">{item.value}</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge variant={item.active ? "default" : "secondary"}>{item.active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <QuestionEditOrCreate question={item} />
                    <ToggleActive question={item} onError={setGlobalErrors} onSuccess={() => setGlobalErrors({})} />
                    <QuestionDeleteForm
                      question={item}
                      onError={setGlobalErrors}
                      onSuccess={() => setGlobalErrors({})}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                No review questions found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

export function ReviewQuestions() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [, setSearchParams] = useSearchParams()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData, reason) => {
      if (reason.type !== "field") return

      if (reason.name === "organisation" || reason.name === "includeInactive") {
        formData.delete("page")
        setSearchParams((urlSearchParams) => {
          urlSearchParams.delete("page")
          return urlSearchParams
        })
      }
    },
    [setSearchParams],
  )

  return (
    <Fragment>
      <div className="flex items-center gap-4">
        {rootData?.organisations != null && rootData.organisations.length > 0 && (
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data?.loadedOrganisation ?? rootData.organisations[0]}
              items={rootData.organisations}
            />
            <FiltersWrapper.Checkbox name="includeInactive" label="Include Inactive" />
          </FiltersWrapper>
        )}
        <div className="flex-1" />
        <QuestionEditOrCreate />
      </div>
      <Outlet />
    </Fragment>
  )
}

// Borrowed from Root Cause to keep consistency
function QuestionEditOrCreate({ question }: { question?: ReviewQuestion }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [open, setOpen] = useState(false)

  const isEditing = question != null

  const closeDialog = useCallback(() => {
    setOpen(false)
  }, [])

  if (data == null || hasPermission("writeAccess") !== true) return null

  // Helper to render the actual button
  const TriggerButton = isEditing ? (
    <Button variant="ghost" size="icon" className="h-8 w-8">
      <PencilIcon className="h-4 w-4" />
      <span className="sr-only">Edit Root Cause</span>
    </Button>
  ) : (
    <Button variant="outline">
      <PlusIcon className="mr-2 h-4 w-4" /> Add New
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isEditing ? (
        /* Outer: Tooltip -> Middle: TooltipTrigger -> Inner: DialogTrigger */
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Edit Question</TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      )}

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add New"} Question</DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <>
                Editing <span className="font-semibold">{question.name}</span>
              </>
            ) : (
              "Create a new question"
            )}
          </DialogDescription>
        </DialogHeader>

        <QuestionEditOrCreateForm question={question} closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function QuestionEditOrCreateForm({ question, closeDialog }: { question?: ReviewQuestion; closeDialog: () => void }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const fetcher = useFetcher<typeof mainAction>()

  const isEditing = question != null

  const rootData = useRouteLoaderData<RootLoader>("root")

  const getCurrentOrganisation = () => {
    if (data?.loadedOrganisation != null && data.loadedOrganisation !== "") return data.loadedOrganisation

    const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
    return filters.parsed?.organisation ?? rootData?.organisations[0]
  }

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<z.infer<typeof QuestionEditOrCreateSchema>>({
    resolver: zodResolver(QuestionEditOrCreateSchema),
    values: {
      action: isEditing ? "UPDATE" : "CREATE",
      organisation: question?.organisation ?? getCurrentOrganisation() ?? "",
      name: question?.name ?? "",
      code: question?.code ?? "",
      value: question?.value ?? "",
    },
  })

  // Combine both sources of errors into one "Summary" shape
  const zodSummary = formatRHFErrors(errors)
  const serverSummary = fetcher.data?.errors // Already in Record<string, string[]> format

  // Merge them (server errors usually take priority if both exist)
  const combinedErrors = { ...zodSummary, ...serverSummary }

  useEffect(() => {
    const result = fetcher.data
    if (result == null) return
    const hasNoValidationErrors = result.errors == null || Object.keys(result.errors).length === 0
    const hasNoGlobalError = result.errorMessage == null

    if (hasNoGlobalError && hasNoValidationErrors) {
      closeDialog()
      if (!isEditing) reset()
    }
  }, [closeDialog, isEditing, reset, fetcher.data])

  if (data == null || !hasPermission("writeAccess")) return null

  const onSubmit = handleSubmit(async (formData) => {
    if (!isEditing) {
      try {
        const response = await fetchAPI<ReviewQuestion>(
          `/api/UnityReview/Question/${data?.loadedOrganisation}/${formData.name}`,
        ).then(handleResponse)

        if (response != null && Object.keys(response).length > 0) {
          setError("name", { message: "That question name already exists, please update the name to make it unique" })
          return
        }
      } catch (error) {
        const response = error as Response
        if (response == null || (response.status !== 404 && response.status !== 204)) {
          console.error(error)
          setError("name", { message: "There was a problem identifying if this is a duplicate entry or not" })
          return
        }
      }
    }

    clearErrors()
    fetcher.submit(formData, { method: "POST" })
  })

  return (
    <fetcher.Form method="POST" className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />

      <ValidationSummary errors={combinedErrors} />
      {isEditing ? (
        <div>
          <label className="mb-2 block text-sm font-medium">Name</label>
          <Input {...register("name")} disabled className="bg-gray-50" />
          <p className="mt-1 text-xs text-gray-500">Name cannot be changed after creation</p>
        </div>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium">Name</label>
          <Input {...register("name")} aria-required="true" placeholder="Enter unique name" />
        </div>
      )}

      <div>
        <label className="mb-2 block text-sm font-medium">Code</label>
        <Input {...register("code")} aria-required="true" placeholder="Enter code" />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">Question Text</label>
        <textarea
          {...register("value")}
          aria-required="true"
          placeholder="Enter question text"
          rows={5}
          className="w-full rounded-md border p-2"
        />
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> {isEditing ? "Updating" : "Creating"}
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit">{isEditing ? "Update" : "Create"}</Button>
      </div>
    </fetcher.Form>
  )
}

ReviewQuestions.Main = Main
ReviewQuestions.loader = loader
ReviewQuestions.action = mainAction
ReviewQuestions.routeId = routeId

Main.action = mainAction
